/* Azha Perfumes — global theme behaviour.
   HTML-first: this script only toggles state and syncs data; it never builds
   page structure. Markup lives in Liquid, visibility is driven by data-state
   attributes + Tailwind variants.

   Modules:
     • Drawer       — open/close any [data-drawer] panel (mobile nav, cart, search)
     • PredictiveSearch — live results + query suggestions inside the search drawer
     • BundleSwap   — one-time cross-fade between two stacked product grids
     • Cart         — AJAX Cart API with bundled section rendering
*/
(function () {
  'use strict';

  /* ---------------------------------------------------------------- utils */
  const on = (el, ev, fn, opts) => el && el.addEventListener(ev, fn, opts);
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  // Resolve once a deferred vendor global (for example Swiper) is available.
  // Section init scripts call: Azha.whenLib('Swiper', () => { ... }).
  window.Azha = window.Azha || {};
  window.Azha.whenLib = window.Azha.whenLib || function (name, cb, tries = 60) {
    if (window[name]) return cb(window[name]);
    if (tries <= 0) return;
    setTimeout(() => window.Azha.whenLib(name, cb, tries - 1), 50);
  };

  const lockScroll = (lock) => {
    document.documentElement.toggleAttribute('data-scroll-lock', lock);
    document.documentElement.style.overflow = lock ? 'hidden' : '';
  };

  /* --------------------------------------------------------------- Drawer */
  // Any [data-drawer-open="id"] opens the [data-drawer="id"] panel.
  // [data-drawer-close] (or backdrop / Escape) closes the open panel.
  const Drawer = {
    open(id) {
      const panel = $(`[data-drawer="${id}"]`);
      if (!panel) return;
      $$('[data-drawer][data-state="open"]').forEach((p) => {
        if (p !== panel) {
          p.setAttribute('data-state', 'closed');
          $$(`[data-drawer-open="${p.getAttribute('data-drawer')}"]`).forEach((trigger) => {
            trigger.setAttribute('aria-expanded', 'false');
          });
        }
      });
      panel.setAttribute('data-state', 'open');
      $$(`[data-drawer-open="${id}"]`).forEach((trigger) => {
        trigger.setAttribute('aria-expanded', 'true');
      });
      // Skip scroll lock for drawers that opt out (e.g. delivery location modal)
      if (!panel.hasAttribute('data-no-scroll-lock')) lockScroll(true);
      document.dispatchEvent(new CustomEvent('drawer:open', { detail: { id } }));
      const focusable = panel.querySelector('[autofocus], button, a, input');
      if (focusable) focusable.focus({ preventScroll: true });
    },
    close(id) {
      const panels = id ? [$(`[data-drawer="${id}"]`)] : $$('[data-drawer][data-state="open"]');
      panels.forEach((p) => {
        if (!p) return;
        p.setAttribute('data-state', 'closed');
        $$(`[data-drawer-open="${p.getAttribute('data-drawer')}"]`).forEach((trigger) => {
          trigger.setAttribute('aria-expanded', 'false');
        });
      });
      // Only release scroll lock when no scroll-locking drawers remain open
      const stillLocked = $$('[data-drawer][data-state="open"]').some(
        (p) => !p.hasAttribute('data-no-scroll-lock')
      );
      if (!stillLocked) lockScroll(false);
      document.dispatchEvent(new CustomEvent('drawer:close', { detail: { id } }));
    },
  };
  window.AzhaDrawer = Drawer;

  $$('[data-drawer]').forEach((panel) => {
    if (!panel.id) panel.id = `${panel.getAttribute('data-drawer')}-drawer`;
    $$(`[data-drawer-open="${panel.getAttribute('data-drawer')}"]`).forEach((trigger) => {
      trigger.setAttribute('aria-controls', panel.id);
      trigger.setAttribute('aria-expanded', panel.getAttribute('data-state') === 'open' ? 'true' : 'false');
    });
  });

  on(document, 'click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      e.preventDefault();
      Drawer.open(opener.getAttribute('data-drawer-open'));
      return;
    }
    if (e.target.closest('[data-drawer-close]') || e.target.hasAttribute('data-drawer-backdrop')) {
      Drawer.close();
    }
  });
  on(document, 'keydown', (e) => {
    if (e.key === 'Escape') Drawer.close();
  });

  /* ------------------------------------------------------------- Confetti */
  // Lightweight canvas burst — no library. Renders inside the given container
  // (the cart drawer) so the celebration stays within the panel. Skips entirely
  // under reduced-motion.
  const Confetti = {
    colors: ['#10312b', '#1b443c', '#c9a86a', '#f4ede2', '#000000'],
    fire(stage, count = 90) {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const host = stage || document.body;
      const fixed = host === document.body;
      if (!fixed && getComputedStyle(host).position === 'static') host.style.position = 'relative';
      const canvas = document.createElement('canvas');
      Object.assign(canvas.style, {
        position: fixed ? 'fixed' : 'absolute',
        inset: '0', width: '100%', height: '100%',
        pointerEvents: 'none', zIndex: '60',
      });
      host.appendChild(canvas);
      const ctx = canvas.getContext('2d');
      const dpr = window.devicePixelRatio || 1;
      const W = (canvas.width = (fixed ? window.innerWidth : host.clientWidth) * dpr);
      const H = (canvas.height = (fixed ? window.innerHeight : host.clientHeight) * dpr);
      const parts = Array.from({ length: count }, (_, i) => ({
        x: W / 2, y: H * 0.12,
        vx: (Math.random() - 0.5) * 14 * dpr,
        vy: (Math.random() * -1 - 0.2) * 14 * dpr,
        size: (4 + Math.random() * 5) * dpr,
        color: this.colors[i % this.colors.length],
        rot: Math.random() * Math.PI,
        vr: (Math.random() - 0.5) * 0.4,
      }));
      const g = 0.45 * dpr;
      const DURATION = 1900;
      let start = null;
      const tick = (t) => {
        if (start === null) start = t;
        const elapsed = t - start;
        const life = Math.max(0, 1 - elapsed / DURATION);
        ctx.clearRect(0, 0, W, H);
        parts.forEach((p) => {
          p.vy += g;
          p.vx *= 0.99;
          p.x += p.vx;
          p.y += p.vy;
          p.rot += p.vr;
          ctx.save();
          ctx.globalAlpha = life;
          ctx.translate(p.x, p.y);
          ctx.rotate(p.rot);
          ctx.fillStyle = p.color;
          ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
          ctx.restore();
        });
        if (elapsed < DURATION) requestAnimationFrame(tick);
        else canvas.remove();
      };
      requestAnimationFrame(tick);
    },
  };

  /* ----------------------------------------------------- Free-delivery bar */
  // CSS-driven shimmer while the cart is open, plus an attention pulse when
  // the free-delivery threshold is reached.
  const FreeShip = {
    start() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const wrap = $('[data-free-shipping]');
      if (wrap) wrap.setAttribute('data-animate', '');
    },
    stop() {
      const wrap = $('[data-free-shipping]');
      if (wrap) wrap.removeAttribute('data-animate');
    },
    celebrate() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const wrap = $('[data-free-shipping]');
      if (!wrap) return;
      wrap.removeAttribute('data-celebrate');
      requestAnimationFrame(() => wrap.setAttribute('data-celebrate', ''));
      window.setTimeout(() => wrap.removeAttribute('data-celebrate'), 800);
    },
  };

  // Celebrate free delivery every time the cart opens while qualified.
  const cartStage = () => $('[data-cart-stage]');
  const freeShipQualified = () => $('[data-free-shipping]')?.getAttribute('data-free-shipping') === 'qualified';
  on(document, 'drawer:open', (e) => {
    if (e.detail.id !== 'cart') return;
    FreeShip.start();
    if (freeShipQualified()) { FreeShip.celebrate(); Confetti.fire(cartStage()); }
  });
  on(document, 'drawer:close', () => FreeShip.stop());

  /* --------------------------------------------------- Predictive search */
  // Drives both the search drawer and the search page: as the visitor types, the
  // [data-predictive-region] (collections + products) is swapped with server-
  // rendered results from the predictive-search section, and query-suggestion
  // chips are filled from the Predictive Search JSON endpoint. Empty input
  // restores the resting markup (best sellers in the drawer, full results on the
  // page). Markup is moved/cloned via DOMParser nodes (never innerHTML) so
  // untrusted strings are never parsed as HTML.
  function initPredictiveSearch() {
    $$('[data-predictive-search]').forEach(initPredictiveRoot);
  }

  function initPredictiveRoot(root) {
    const input = $('[data-predictive-input]', root);
    const region = $('[data-predictive-region]', root);
    const queriesWrap = $('[data-predictive-queries-wrap]', root);
    const queries = $('[data-predictive-queries]', root);
    const skeleton = $('[data-predictive-skeleton]', root);
    if (!input || !region || !queriesWrap || !queries) return;

    const ROUTES = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const SECTION_ID = 'predictive-search';
    const parse = (html) => new DOMParser().parseFromString(html, 'text/html');
    let restingNodes = Array.from(region.childNodes).map((n) => n.cloneNode(true));
    const isDrawer = !!root.closest('[data-drawer]');
    let controller;
    let debounce;

    const hideQueries = () => {
      queries.replaceChildren();
      queriesWrap.classList.add('hidden');
    };

    const restoreRegion = () => {
      region.replaceChildren(...restingNodes.map((n) => n.cloneNode(true)));
      document.dispatchEvent(new CustomEvent('predictive:rendered', { detail: { region } }));
    };

    const showSkeleton = () => {
      if (skeleton) region.replaceChildren(skeleton.content.cloneNode(true));
    };

    const reset = () => {
      restoreRegion();
      hideQueries();
    };

    on(document, 'predictive:resting-updated', (event) => {
      if (!event.detail || event.detail.region !== region) return;
      restingNodes = event.detail.nodes.map((node) => node.cloneNode(true));
    });

    // Wrap matched query words in product-card titles with a yellow <mark>.
    const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const highlightTitles = (term) => {
      const words = term.trim().split(/\s+/).filter(Boolean).map(escapeRegExp);
      if (!words.length) return;
      const re = new RegExp(`(${words.join('|')})`, 'gi');
      region.querySelectorAll('[data-product-card] h3 a').forEach((el) => {
        const text = el.textContent;
        const matches = Array.from(text.matchAll(re));
        if (!matches.length) return;
        const frag = document.createDocumentFragment();
        let last = 0;
        matches.forEach((m) => {
          if (m.index > last) frag.appendChild(document.createTextNode(text.slice(last, m.index)));
          const mark = document.createElement('mark');
          mark.className = 'search-hl';
          mark.textContent = m[0];
          frag.appendChild(mark);
          last = m.index + m[0].length;
        });
        if (last < text.length) frag.appendChild(document.createTextNode(text.slice(last)));
        el.replaceChildren(frag);
      });
    };

    const run = (term) => {
      if (controller) controller.abort();
      controller = new AbortController();
      const signal = controller.signal;
      const q = encodeURIComponent(term);
      showSkeleton();

      // Products + collections — Section Rendering API (reuses Liquid cards).
      // - unavailable_products=last keeps sold-out matches visible after every
      //   available product.
      // - fields adds `body` (description) + `tag` so fragrance notes like
      //   "vetiver" — which live in descriptions/tags, not titles — match here
      //   the way they do on the full search page. (Default only searches
      //   title, product_type, variants.title, vendor.)
      fetch(
        `${ROUTES}search/suggest?q=${q}&section_id=${SECTION_ID}&resources[type]=product,collection&resources[limit]=10&resources[limit_scope]=each&resources[options][unavailable_products]=last&resources[options][fields]=title,product_type,variants.title,vendor,tag,body`,
        { signal }
      )
        .then((r) => (r.ok ? r.text() : Promise.reject(r.status)))
        .then((html) => {
          const node = parse(html).querySelector(`#shopify-section-${SECTION_ID}`);
          if (node) {
            region.replaceChildren(...Array.from(node.childNodes));
            highlightTitles(term);
            document.dispatchEvent(new CustomEvent('predictive:rendered', { detail: { region } }));
          }
        })
        .catch(() => {
          // Leave the skeleton in place if a newer request superseded this one.
          if (!signal.aborted) restoreRegion();
        });

      // Query suggestions — JSON endpoint (not exposed to Liquid section rendering).
      fetch(`${ROUTES}search/suggest.json?q=${q}&resources[type]=query&resources[limit]=6`, { signal })
        .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
        .then((data) => {
          const list = (data.resources && data.resources.results && data.resources.results.queries) || [];
          if (!list.length) return hideQueries();
          const frag = document.createDocumentFragment();
          list.forEach((item) => {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className =
              'inline-flex items-center rounded-full border border-border px-3 py-1.5 text-xs text-primary transition-colors hover:border-primary/40 hover:bg-primary/5';
            // Wrap the label in a single span so the flex button has one flex
            // item — otherwise the whitespace text nodes between words collapse
            // ("eid gift set" -> "eidgiftset"). styled_text is Shopify-generated
            // highlight markup, parsed to nodes rather than assigned via innerHTML.
            const label = document.createElement('span');
            if (item.styled_text) {
              const parsed = parse(`<span>${item.styled_text}</span>`).body.firstChild;
              label.append(...Array.from(parsed.childNodes));
            } else {
              label.textContent = item.text;
            }
            btn.appendChild(label);
            // Fill the search bar with the suggestion and search in place.
            btn.addEventListener('click', () => {
              clearTimeout(debounce);
              input.value = item.text;
              input.focus();
              run(item.text);
            });
            frag.appendChild(btn);
          });
          queries.replaceChildren(frag);
          queriesWrap.classList.remove('hidden');
        })
        .catch(() => {});
    };

    on(input, 'input', () => {
      const term = input.value.trim();
      clearTimeout(debounce);
      if (!term) return reset();
      debounce = setTimeout(() => run(term), 250);
    });

    if (isDrawer) {
      // Restore the resting state whenever the search drawer closes.
      on(document, 'drawer:close', (e) => {
        if (e.detail && e.detail.id && e.detail.id !== 'search') return;
        input.value = '';
        if (controller) controller.abort();
        clearTimeout(debounce);
        reset();
      });
    } else if (input.value.trim()) {
      // Search page: highlight the server-rendered results for the current term.
      highlightTitles(input.value.trim());
    }
  }

  // The search drawer ships only a small skeleton. Its merchandising content is
  // requested once, on first open, so every unrelated page avoids rendering and
  // downloading eight hidden product cards.
  function initLazySearchDrawer() {
    on(document, 'drawer:open', (event) => {
      if (!event.detail || event.detail.id !== 'search') return;
      const drawer = $('[data-drawer="search"]');
      const root = drawer && $('[data-predictive-search]', drawer);
      const region = root && $('[data-predictive-region]', root);
      const input = root && $('[data-predictive-input]', root);
      if (!region || !region.dataset.restingUrl || region.dataset.restingLoaded || region.dataset.restingLoading) return;
      region.dataset.restingLoading = 'true';
      fetch(region.dataset.restingUrl)
        .then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const content = doc.querySelector('[data-search-resting-content]');
          if (!content) return;
          const nodes = Array.from(content.childNodes).map((node) => node.cloneNode(true));
          region.dataset.restingLoaded = 'true';
          document.dispatchEvent(new CustomEvent('predictive:resting-updated', { detail: { region, nodes } }));
          if (!input || !input.value.trim()) {
            region.replaceChildren(...nodes.map((node) => node.cloneNode(true)));
            document.dispatchEvent(new CustomEvent('predictive:rendered', { detail: { region } }));
          }
        })
        .catch(() => {})
        .finally(() => { delete region.dataset.restingLoading; });
    });
  }

  /* ------------------------------------------------- Collections slider */
  // Search-drawer collections row (lg+): arrow buttons scroll the horizontal
  // list; their disabled state reflects scroll position (both disabled = nothing
  // to scroll, so CSS fades them out). Event-driven so it survives the
  // predictive-search content swaps without re-binding listeners.
  function syncCollectionsSlider(slider) {
    if (!slider) return;
    const track = $('[data-collections-track]', slider);
    if (!track) return;
    const prev = $('[data-collections-prev]', slider);
    const next = $('[data-collections-next]', slider);
    const max = track.scrollWidth - track.clientWidth;
    if (prev) prev.disabled = track.scrollLeft <= 1;
    if (next) next.disabled = track.scrollLeft >= max - 1;
  }

  const syncAllCollectionsSliders = () => $$('[data-collections-slider]').forEach(syncCollectionsSlider);

  function initCollectionsSlider() {
    on(document, 'click', (e) => {
      const btn = e.target.closest('[data-collections-prev], [data-collections-next]');
      if (!btn) return;
      const slider = btn.closest('[data-collections-slider]');
      const track = slider && $('[data-collections-track]', slider);
      if (!track) return;
      const dir = btn.matches('[data-collections-next]') ? 1 : -1;
      track.scrollBy({ left: dir * track.clientWidth * 0.85, behavior: 'smooth' });
    });
    // Scroll events don't bubble — capture so swapped-in tracks are covered too.
    document.addEventListener(
      'scroll',
      (e) => {
        const t = e.target;
        if (t && t.nodeType === 1 && t.matches && t.matches('[data-collections-track]')) {
          syncCollectionsSlider(t.closest('[data-collections-slider]'));
        }
      },
      true
    );
    on(document, 'predictive:rendered', syncAllCollectionsSliders);
    on(document, 'drawer:open', (e) => {
      if (e.detail && e.detail.id === 'search') syncAllCollectionsSliders();
    });
    on(window, 'resize', syncAllCollectionsSliders);
  }

  /* ------------------------------------------------------------- Collection */
  // PLP filtering/sorting via the Section Rendering API + infinite scroll.
  const Collection = {
    init() {
      $$('[data-collection]').forEach((root) => Collection.bind(root));
    },

    bind(root) {
      if (root.dataset.inited) return;
      root.dataset.inited = '1';
      const sectionId = root.id;
      let controller = null;
      let loadingMore = false;
      let reqId = 0;
      let io = null;

      function observeSentinel() {
        if (io) io.disconnect();
        const sentinel = root.querySelector('[data-collection-sentinel]');
        if (!sentinel) return;
        io = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !loadingMore) {
              loadingMore = true;
              const next = sentinel.dataset.nextUrl;
              const link = sentinel.querySelector('[data-collection-loadmore]');
              const spin = sentinel.querySelector('[data-collection-spinner]');
              if (link) link.hidden = true;
              if (spin) spin.hidden = false;
              render(next, { append: true }).finally(() => {
                loadingMore = false;
              });
            }
          });
        }, { rootMargin: '600px' });
        io.observe(sentinel);
      }

      const render = async (url, { append = false } = {}) => {
        const my = ++reqId;
        if (controller) controller.abort();
        controller = new AbortController();
        // Dim the current grid while the new results load (filter/sort feedback).
        if (!append) root.setAttribute('data-loading', '');
        const fetchUrl = `${url}${url.includes('?') ? '&' : '?'}section_id=${sectionId}`;
        try {
          const res = await fetch(fetchUrl, { signal: controller.signal });
          const html = new DOMParser().parseFromString(await res.text(), 'text/html');

          if (append) {
            const newGrid = html.querySelector('[data-product-grid]');
            const curGrid = root.querySelector('[data-product-grid]');
            if (newGrid && curGrid) {
              const cards = Array.from(newGrid.children);
              const firstSoldOut = curGrid.querySelector(':scope > [data-sold-out]');
              cards.filter((card) => !card.hasAttribute('data-sold-out')).forEach((card) => {
                curGrid.insertBefore(card, firstSoldOut);
              });
              curGrid.append(...cards.filter((card) => card.hasAttribute('data-sold-out')));
            }
            const oldSentinel = root.querySelector('[data-collection-sentinel]');
            const newSentinel = html.querySelector('[data-collection-sentinel]');
            if (curGrid) curGrid.toggleAttribute('data-has-more', Boolean(newSentinel));
            if (newSentinel && oldSentinel) {
              oldSentinel.replaceWith(newSentinel);
            } else if (oldSentinel) {
              oldSentinel.remove();
            }
            observeSentinel();
          } else {
            ['[data-collection-toolbar]', '[data-collection-grid]'].forEach((sel) => {
              const next = html.querySelector(sel);
              const cur = root.querySelector(sel);
              if (next && cur) cur.replaceWith(next);
            });
            const newForms = html.querySelectorAll('[data-collection-filters]');
            root.querySelectorAll('[data-collection-filters]').forEach((form, i) => {
              if (newForms[i]) form.replaceWith(newForms[i]);
            });
            observeSentinel();
          }
        } catch (e) {
          if (e.name !== 'AbortError') console.error('[collection]', e);
        } finally {
          // Only the latest request clears the loading state (avoids abort races).
          if (!append && my === reqId) root.removeAttribute('data-loading');
        }
      };

      const buildUrl = (form) => {
        const params = new URLSearchParams(form ? new FormData(form) : undefined);
        const sort = root.querySelector('[data-collection-sort]');
        if (sort) params.set('sort_by', sort.value);
        for (const [k, v] of [...params]) if (v === '') params.delete(k);
        const qs = params.toString();
        return `${root.dataset.collectionUrl}${qs ? '?' + qs : ''}`;
      };

      const apply = (form) => {
        const url = buildUrl(form);
        history.pushState({ collectionUrl: url }, '', url);
        render(url);
      };

      on(root, 'change', (e) => {
        if (e.target.matches('[data-collection-sort]')) {
          // Keep every sort control (toolbar + sticky bar) on the same value.
          const val = e.target.value;
          root.querySelectorAll('[data-collection-sort]').forEach((s) => {
            if (s.value !== val) s.value = val;
          });
          const form = root.querySelector('[data-collection-filters]');
          apply(form);
          return;
        }
        const form = e.target.closest('[data-collection-filters]');
        if (form) apply(form);
      });

      on(root, 'input', (e) => {
        if (e.target.matches('input[type="number"]')) {
          clearTimeout(root._priceT);
          const form = e.target.closest('[data-collection-filters]');
          root._priceT = setTimeout(() => form && apply(form), 600);
        }
      });

      on(root, 'click', (e) => {
        const link = e.target.closest('[data-collection-link]');
        if (link) {
          e.preventDefault();
          history.pushState({ collectionUrl: link.href }, '', link.href);
          render(link.href);
          if (window.AzhaDrawer) window.AzhaDrawer.close('collection-filters');
        }
      });

      on(window, 'popstate', () => {
        render(window.location.href);
      });

      observeSentinel();
    },
  };
  window.Azha.Collection = Collection;

  /* ----------------------------------------------------------------- Cart */
  const Cart = {
    sectionsToRender: ['cart-drawer'],
    route(action) {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      return `${root}cart/${action}.js`;
    },
    requestBody(data) {
      return {
        ...data,
        sections: this.sectionsToRender.join(','),
        sections_url: window.location.pathname,
      };
    },
    setButtonLoading(button, loading) {
      if (!button) return;
      button.toggleAttribute('data-loading', loading);
      button.setAttribute('aria-busy', String(loading));
      button.disabled = loading;
    },

    // Shopify returns a buyer-friendly `description` (e.g. stock limits); fall
    // back to the localized generic message rather than a technical string.
    errorMessage(err) {
      const friendly = err && typeof err === 'object' ? err.description || err.message : null;
      const generic = $('[data-section-render="cart-drawer"]')?.getAttribute('data-cart-error-generic');
      return friendly || generic || 'Something went wrong.';
    },
    showLineError(key, message) {
      const li = $$('[data-cart-item]').find((line) => line.getAttribute('data-line-key') === key);
      const slot = li && li.querySelector('[data-line-error]');
      if (!slot) return;
      slot.textContent = message;
      slot.hidden = false;
      clearTimeout(slot._dismiss);
      slot._dismiss = setTimeout(() => { slot.hidden = true; slot.textContent = ''; }, 5000);
    },

    setCount(el, value) {
      el.textContent = value;
      el.toggleAttribute('data-empty', value === 0);
    },
    setAllCounts(count) {
      $$('[data-cart-count]').forEach((el) => this.setCount(el, count));
      const dc = $('[data-cart-drawer-count]');
      if (dc) dc.textContent = `(${count})`;
      const auth = $('[data-cart-authoritative]');
      if (auth) auth.textContent = count;
    },
    syncCount() {
      const auth = $('[data-cart-authoritative]');
      if (auth) this.setAllCounts(parseInt(auth.textContent, 10) || 0);
    },

    async add(items, button) {
      this.setButtonLoading(button, true);
      try {
        const res = await fetch(this.route('add'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(this.requestBody({ items })),
        });
        const data = await res.json();
        if (!res.ok) throw data;
        this.setButtonLoading(button, false);
        this.flyToCart(button);
        this.renderSection(data.sections);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
      } catch (err) {
        document.dispatchEvent(new CustomEvent('cart:error', { detail: err }));
      } finally {
        this.setButtonLoading(button, false);
      }
    },
    async change(key, quantity, button) {
      const line = button && button.closest('[data-cart-item]');
      if (line && line.hasAttribute('data-loading')) return;
      if (line) {
        line.setAttribute('data-loading', '');
        line.setAttribute('aria-busy', 'true');
        $$('button', line).forEach((control) => { control.disabled = true; });
      }
      try {
        const res = await fetch(this.route('change'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(this.requestBody({ id: key, quantity: Math.max(0, quantity) })),
        });
        let data = await res.json();
        if (!res.ok) throw data;
        const gift = data.items && data.items.find((item) => item.properties && item.properties._free_gift);
        const giftThreshold = parseInt($('[data-free-gift]')?.getAttribute('data-fg-threshold'), 10) || 0;
        if (gift && giftThreshold > 0 && data.total_price < giftThreshold) {
          const giftRes = await fetch(this.route('change'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(this.requestBody({ id: gift.key, quantity: 0 })),
          });
          data = await giftRes.json();
          if (!giftRes.ok) throw data;
        }
        this.renderSection(data.sections);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
      } catch (err) {
        document.dispatchEvent(new CustomEvent('cart:error', { detail: err }));
        this.showLineError(key, this.errorMessage(err));
      } finally {
        if (line && line.isConnected) {
          line.removeAttribute('data-loading');
          line.removeAttribute('aria-busy');
          $$('button', line).forEach((control) => { control.disabled = false; });
        }
      }
    },

    renderSection(sections) {
      if (!sections) return;
      const wasQualified = freeShipQualified();
      const openDrawers = $$('[data-drawer][data-state="open"]').map((d) => d.getAttribute('data-drawer'));
      this.sectionsToRender.forEach((id) => {
        const html = sections[id];
        if (html == null) return;
        const target = $(`[data-section-render="${id}"]`);
        if (!target) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector(`[data-section-render="${id}"]`);
        if (fresh) target.replaceWith(fresh);
      });
      openDrawers.forEach((id) => {
        const d = $(`[data-drawer="${id}"]`);
        if (d) d.setAttribute('data-state', 'open');
      });
      this.syncCount();
      const drawerOpen = !!$('[data-drawer="cart"][data-state="open"]');
      if (drawerOpen) FreeShip.start();
      if (!wasQualified && drawerOpen && freeShipQualified()) { FreeShip.celebrate(); Confetti.fire(cartStage()); }
      document.dispatchEvent(new CustomEvent('cart:rendered'));
    },

    flyToCart(opener) {
      if (!opener) return;
      const card = opener.closest('[data-product-card]');
      let sourceImg = card ? card.querySelector('img') : null;
      if (!sourceImg) sourceImg = $('[data-pdp-main] .swiper-slide-active img') || $('[data-pdp-media] img');
      if (!sourceImg) sourceImg = opener.closest('form')?.querySelector('img');
      const target = $$('[data-cart-icon]').find((el) => el.offsetParent !== null) || $('[data-cart-icon]');
      if (!sourceImg || !target || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const from = sourceImg.getBoundingClientRect();
      const to = target.getBoundingClientRect();

      const ghost = document.createElement('div');
      const img = sourceImg.cloneNode(true);
      Object.assign(img.style, { width: '100%', height: '100%', objectFit: 'cover', display: 'block', borderRadius: '12px' });
      img.removeAttribute('loading');
      ghost.appendChild(img);
      Object.assign(ghost.style, {
        position: 'fixed', left: `${from.left}px`, top: `${from.top}px`,
        width: `${from.width}px`, height: `${from.height}px`,
        padding: '4px', borderRadius: '14px', overflow: 'hidden',
        background: '#fff', boxShadow: '0 12px 32px rgba(0,0,0,0.18)',
        zIndex: 9998, pointerEvents: 'none', willChange: 'transform, opacity',
      });
      document.body.appendChild(ghost);

      const end = 28;
      const endLeft = to.left + to.width / 2 - end / 2;
      const endTop = to.top + to.height / 2 - end / 2;
      const flight = ghost.animate(
        [
          { transform: 'translate3d(0, 0, 0) scale(1)', opacity: 1 },
          { transform: `translate3d(${endLeft - from.left}px, ${endTop - from.top}px, 0) scale(${end / from.width})`, opacity: 0.25 },
        ],
        { duration: 750, easing: 'cubic-bezier(.4,0,1,1)', fill: 'forwards' }
      );
      flight.finished.catch(() => {}).finally(() => ghost.remove());
      target.animate(
        [{ transform: 'scale(1)' }, { transform: 'scale(1.25)' }, { transform: 'scale(1)' }],
        { duration: 360, delay: 560, easing: 'ease-out' }
      );
    },
  };
  window.AzhaCart = Cart;

  /* ------------------------------------------------------------- Free gift */
  // Single-select free discovery set. Mutations use the same server-rendered cart
  // flow as standard cart additions and removals.
  const FreeGift = {
    wrap() { return $('[data-free-gift]'); },
    add(variantId, button) {
      const wrap = this.wrap();
      if (!variantId || !wrap || wrap.getAttribute('data-free-gift') !== 'unlocked') return;
      return Cart.add([{ id: variantId, quantity: 1, properties: { _free_gift: '1' } }], button);
    },
    remove(button) {
      const wrap = this.wrap();
      const key = (wrap && wrap.getAttribute('data-fg-key')) || '';
      if (!key) return;
      return Cart.change(key, 0, button);
    },
  };

  on(document, 'click', (e) => {
    const addBtn = e.target.closest('[data-fg-add]');
    if (addBtn) { FreeGift.add(addBtn.getAttribute('data-variant'), addBtn); return; }
    const removeBtn = e.target.closest('[data-fg-remove]');
    if (removeBtn) FreeGift.remove(removeBtn);
  });

  // Wire add-to-cart forms + plus buttons declaratively.
  on(document, 'submit', (e) => {
    const addForm = e.target.closest('form[data-cart-add]');
    if (addForm) {
      e.preventDefault();
      const id = addForm.querySelector('[name="id"]').value;
      const qty = parseInt(addForm.querySelector('[name="quantity"]')?.value, 10) || 1;
      const button = e.submitter || addForm._cartSubmitter || addForm.querySelector('[type="submit"]');
      addForm._cartSubmitter = null;
      Cart.add([{ id, quantity: qty }], button);
    }
  });

  on(document, 'click', (e) => {
    const minus = e.target.closest('[data-line-minus]');
    const plus = e.target.closest('[data-line-plus]');
    const remove = e.target.closest('[data-line-remove]');
    const btn = minus || plus || remove;
    if (!btn) return;
    const li = btn.closest('[data-cart-item]');
    const key = li?.getAttribute('data-line-key');
    if (!key) return;
    const current = parseInt(li.getAttribute('data-quantity'), 10) || 0;
    if (remove) Cart.change(key, 0, btn);
    else Cart.change(key, plus ? current + 1 : Math.max(0, current - 1), btn);
  });

  /* ------------------------------------------------------ Cart recommendations */
  // Lazy-loads the "you may also like" panel beside the cart drawer (desktop).
  // Seeded from the first cart item; results are cached per seed and re-injected
  // after each cart re-render (which wipes the panel markup). An empty result
  // hides the panel. Network is hit at most once per seed.
  const CartRecs = {
    cache: {},
    loading: {},
    // [data-cart-recs] (the desktop side panel) carries the route + seed; it's in
    // the DOM whenever the cart has items, even while hidden on mobile.
    config() { return $('[data-cart-recs]'); },
    // Every container to fill: the desktop panel list and the mobile slider.
    lists() { return $$('[data-cart-recs-list]'); },
    // Wrappers to hide when there are no recommendations.
    wrappers() { return $$('[data-cart-recs], [data-cart-recs-mobile]'); },

    populate() {
      const cfg = this.config();
      if (!cfg || !this.lists().length) return;
      const seed = cfg.getAttribute('data-seed');
      if (!seed) return;
      const cached = this.cache[seed];
      if (cached !== undefined) { this.inject(cached); return; }
      if (!this.loading[seed]) this.fetch(seed, cfg);
    },

    // Fill every recommendations container from cached card markup; hide the
    // wrappers when there's nothing to show.
    inject(html) {
      const doc = new DOMParser().parseFromString(html || '', 'text/html');
      const source = doc.querySelector('[data-cart-recs-list]');
      const cards = source ? Array.from(source.children) : [];
      this.lists().forEach((list) => {
        list.replaceChildren();
        cards.forEach((card) => list.appendChild(card.cloneNode(true)));
      });
      const has = cards.length > 0;
      this.wrappers().forEach((el) => { el.style.display = has ? '' : 'none'; });
    },

    async fetch(seed, cfg) {
      const route = cfg.getAttribute('data-recs-route');
      if (!route) return;
      this.loading[seed] = true;
      try {
        const res = await fetch(`${route}?section_id=cart-recommendations&product_id=${seed}&intent=related`);
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const list = doc.querySelector('[data-cart-recs-list]');
        const cards = list && list.children.length ? list.outerHTML : '';
        this.cache[seed] = cards;
        // The cart may have re-rendered mid-fetch — only paint if the seed still matches.
        if (this.config()?.getAttribute('data-seed') === seed) this.inject(cards);
      } catch (e) {
        // Leave the skeleton; allow a retry on the next open.
      } finally {
        this.loading[seed] = false;
      }
    },
  };
  on(document, 'drawer:open', (e) => { if (e.detail.id === 'cart') CartRecs.populate(); });
  on(document, 'cart:rendered', () => CartRecs.populate());

  /* ------------------------------------------------ Zoko accessibility */
  const ZOKO_ICON_SELECTOR = '#zoko-8aec0844-b70f-11ec-b909-0242ac120002 img[src*="wp_widget_icon"]';

  function setZokoIconAlt(root = document) {
    const icons = root.matches?.(ZOKO_ICON_SELECTOR) ? [root] : $$(ZOKO_ICON_SELECTOR, root);
    icons.forEach((icon) => {
      if (!icon.getAttribute('alt')) icon.setAttribute('alt', 'Chat with us on WhatsApp');
    });
  }

  let styledSmartBotRoot;

  function positionSmartBotIcon() {
    const root = document.getElementById('smart-bot-container')?.shadowRoot;
    if (!root || root === styledSmartBotRoot) return;

    const style = document.createElement('style');
    style.textContent = '@media (max-width: 1023.98px) { [aria-label="Open chat window"] { bottom: calc(20px + 10vh) !important; } }';
    root.append(style);
    styledSmartBotRoot = root;
  }

  const zokoObserver = new MutationObserver((mutations) => {
    positionSmartBotIcon();
    mutations.forEach((mutation) => {
      setZokoIconAlt(mutation.target);
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) setZokoIconAlt(node);
      });
    });
  });

  setZokoIconAlt();
  positionSmartBotIcon();
  zokoObserver.observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['src', 'alt'],
  });

  /* --------------------------------------------------------------- bootstrap */
  function boot() {
    initPredictiveSearch();
    initLazySearchDrawer();
    initCollectionsSlider();
    Collection.init();
    // Reconcile every cart badge to the drawer's authoritative count, which
    // excludes the slider-only free gift (badges render raw cart.item_count).
    Cart.syncCount();
  }
  boot();
})();
